import { Check, Copy, DownloadSimple, Info } from "@phosphor-icons/react";
import { useState } from "react";
import { text } from "../i18n.js";
import { formatBytes } from "../lib/format.js";
import type { CommunityVersionView, Locale } from "../types.js";
import { StatusBadge } from "./StatusBadge.js";

export function DownloadPanel({ locale, version }: { readonly locale: Locale; readonly version: CommunityVersionView }) {
  const t = text(locale);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const downloadable = (version.supportStatus === "verified" || version.supportStatus === "needs-reverification") && version.archive;
  async function copyHash() {
    if (!version.archive) return;
    try {
      await navigator.clipboard.writeText(version.archive.sha256);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }
  return (
    <aside className="download-panel" aria-label={locale === "zh-CN" ? "下载与校验" : "Download and integrity"}>
      <div className="download-panel-heading">
        <div><span>{t.version}</span><strong>{version.version}</strong></div>
        <StatusBadge locale={locale} status={version.supportStatus} withHelp />
      </div>
      <p className="status-explanation">{t.statusHelp[version.supportStatus]}</p>
      <dl className="download-facts">
        <div><dt>{t.fileSize}</dt><dd>{version.archive ? formatBytes(version.archive.bytes, locale) : "—"}</dd></div>
        <div><dt>{t.schema}</dt><dd>v{version.themeSchemaVersion}</dd></div>
        <div><dt>{t.compatibility}</dt><dd>OpenChatGPTSkin ≥ {version.minimumAppVersion}</dd></div>
        <div><dt>{t.lastVerified}</dt><dd>{version.lastVerifiedAppVersion}</dd></div>
      </dl>
      {version.archive ? (
        <div className="hash-block">
          <span>{t.sha256}</span>
          <code>{version.archive.sha256}</code>
          <button type="button" onClick={copyHash}>
            {copyState === "copied" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            {copyState === "copied" ? t.copied : t.copyHash}
          </button>
          {copyState === "failed" ? <p role="alert">{locale === "zh-CN" ? "浏览器拒绝访问剪贴板，请手动选择哈希。" : "The browser denied clipboard access. Select the hash manually."}</p> : null}
        </div>
      ) : null}
      {downloadable ? (
        <a className="download-button" href={downloadable.url}>
          <DownloadSimple aria-hidden="true" weight="bold" />{t.download}
        </a>
      ) : <span className="download-button disabled" aria-disabled="true">{t.downloadUnavailable}</span>}
      <p className="download-hint"><Info aria-hidden="true" />{t.downloadHint}</p>
    </aside>
  );
}
